import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { getContentService } from '../service/ServiceRegistry';
import type { InternationalContentActionResponse } from '../types/api/settings';

/**
 * Hook for international content activate/deactivate actions
 */
export function useInternationalContentActions() {
  const contentService = getContentService();

  const activateInternationalContent = useCallback(
    async (): Promise<InternationalContentActionResponse> => {
      try {
        const result = await contentService.activateInternationalContent();
        const message =
          result.updatedCount !== undefined
            ? `International content activated successfully. ${result.updatedCount} item(s) updated.`
            : 'International content activated successfully.';
        toast.success(message);
        if (result.warningMessage) {
          toast(result.warningMessage, { icon: '⚠️' });
        }
        return result;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to activate international content';
        toast.error(message);
        throw error;
      }
    },
    [contentService]
  );

  const deactivateInternationalContent = useCallback(
    async (): Promise<InternationalContentActionResponse> => {
      try {
        const result = await contentService.deactivateInternationalContent();
        const message =
          result.updatedCount !== undefined
            ? `International content deactivated successfully. ${result.updatedCount} item(s) updated.`
            : 'International content deactivated successfully.';
        toast.success(message);
        if (result.warningMessage) {
          toast(result.warningMessage, { icon: '⚠️' });
        }
        return result;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to deactivate international content';
        toast.error(message);
        throw error;
      }
    },
    [contentService]
  );

  return {
    activateInternationalContent,
    deactivateInternationalContent,
  };
}
